import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';
import { useAuth, IMPERSONABLE_ROLES } from '../../context/AuthContext';

const {
  FiShield, FiPlus, FiTrash2, FiEdit3, FiCheck, FiX,
  FiUser, FiToggleLeft, FiToggleRight, FiAlertTriangle,
  FiRefreshCw, FiSave, FiLock, FiCrown,
  FiMail, FiClock, FiSearch, FiZap,
  FiEye, FiHeart, FiHome, FiShoppingBag, FiGitBranch, FiBriefcase,
} = FiIcons;

const ICON_MAP = {
  FiEye, FiHeart, FiHome, FiShoppingBag, FiGitBranch, FiBriefcase,
};

const PERMISSION_GROUPS = [
  {
    group: 'Content',
    perms: [
      { key: 'moderation',   label: 'Global Moderation',       desc: 'Approve/reject tributes and content' },
      { key: 'analytics',    label: 'View Analytics',          desc: 'Access site-wide analytics' },
      { key: 'memorials',    label: 'Memorial Management',     desc: 'Edit and manage all memorials' },
    ],
  },
  {
    group: 'Commerce',
    perms: [
      { key: 'merchants',    label: 'Merchant Management',     desc: 'Approve/reject merchant applications' },
      { key: 'payments',     label: 'Payment Settings',        desc: 'Configure payment gateways' },
    ],
  },
  {
    group: 'Platform',
    perms: [
      { key: 'webinars',     label: 'Webinar Integrations',    desc: 'Manage meeting platform configs' },
      { key: 'streaming',    label: 'Streaming Integrations',  desc: 'Manage streaming platform configs' },
      { key: 'defaults',     label: 'Memorial Defaults',       desc: 'Set site-wide memorial defaults' },
      { key: 'scheduling',   label: 'Appointment Scheduling',  desc: 'Manage appointment queues' },
    ],
  },
];

const ALL_PERMS = PERMISSION_GROUPS.flatMap(g => g.perms.map(p => p.key));

const DEFAULT_PERMISSIONS = {
  moderation: true, analytics: true, memorials: true,
  merchants: false, payments: false,
  webinars: false, streaming: false, defaults: false, scheduling: true,
};

const parsePerms = (raw) => {
  try { return JSON.parse(raw || '{}'); } catch { return {}; }
};

const Toggle = ({ active, onToggle }) => (
  <button type="button" onClick={onToggle}
    className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${active ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${active ? 'right-0.5' : 'left-0.5'}`} />
  </button>
);

const RoleImpersonationSection = () => {
  const { startImpersonation, stopImpersonation, impersonatedRole, isImpersonating } = useAuth();

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-8 rounded-[32px]
                      shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="flex items-center gap-4 mb-4 relative z-10">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="font-serif text-2xl">Role Impersonation</h3>
            <p className="text-white/60 text-[10px] font-black uppercase tracking-widest">
              Super Admin Testing Tool
            </p>
          </div>
        </div>
        <p className="text-white/70 text-sm font-light leading-relaxed relative z-10">
          Impersonate any platform role to verify the UI experience, test permissions,
          and troubleshoot role-specific issues. Your identity and data are never affected.
        </p>
      </div>

      {isImpersonating && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between p-5 bg-orange-50 border-2 border-orange-200 rounded-[24px]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-orange-800">
                Currently impersonating: <span className="text-orange-600">
                  {IMPERSONABLE_ROLES.find(r => r.id === impersonatedRole)?.label}
                </span>
              </p>
              <p className="text-[10px] text-orange-600 font-light">
                Orange bar visible at top of page. Your real identity is preserved.
              </p>
            </div>
          </div>
          <button onClick={stopImpersonation}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-orange-200
                       text-orange-600 rounded-xl text-[10px] font-black uppercase tracking-widest
                       hover:bg-orange-50 transition-all">
            <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
            Exit
          </button>
        </motion.div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {IMPERSONABLE_ROLES.map(role => {
          const IconComp = ICON_MAP[role.icon];
          const isActive = impersonatedRole === role.id;
          return (
            <motion.div key={role.id} layout
              className={`relative p-6 rounded-[28px] border-2 transition-all ${
                isActive
                  ? 'border-orange-400 bg-orange-50 shadow-lg shadow-orange-100'
                  : 'border-brand-dark bg-white hover:border-accent-gold/50'
              }`}>
              {isActive && (
                <div className="absolute top-3 right-3">
                  <span className="text-[8px] px-2 py-1 bg-orange-500 text-white rounded-full font-black uppercase tracking-widest">
                    Active
                  </span>
                </div>
              )}
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border mb-4 ${role.color}`}>
                {IconComp && <SafeIcon icon={IconComp} className="w-6 h-6" />}
              </div>
              <h4 className="font-serif text-accent-navy text-lg mb-1">{role.label}</h4>
              <p className="text-xs text-text-muted font-light leading-relaxed mb-4">{role.desc}</p>
              <button
                onClick={() => isActive ? stopImpersonation() : startImpersonation(role.id)}
                className={`w-full py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                  isActive
                    ? 'bg-white border-2 border-orange-300 text-orange-600 hover:bg-orange-50'
                    : 'bg-accent-navy text-white hover:bg-accent-gold'
                }`}>
                {isActive ? 'Stop Preview' : 'Preview as this Role'}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const AddAdminModal = ({ isOpen, onClose, onAdded }) => {
  const { user } = useAuth();
  const [email, setEmail]   = useState('');
  const [name, setName]     = useState('');
  const [notes, setNotes]   = useState('');
  const [perms, setPerms]   = useState({ ...DEFAULT_PERMISSIONS });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const togglePerm = (key) => setPerms(p => ({ ...p, [key]: !p[key] }));
  const grantAll   = () => setPerms(Object.fromEntries(ALL_PERMS.map(k => [k, true])));
  const revokeAll  = () => setPerms(Object.fromEntries(ALL_PERMS.map(k => [k, false])));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError('Email is required');
    setSaving(true); setError('');
    try {
      const res = await writeFetch('create/site_admins_1741165000', {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(),
          user_id: email.trim().toLowerCase(),
          email: email.trim().toLowerCase(),
          name: name.trim(),
          role: 'admin',
          permissions: JSON.stringify(perms),
          granted_by: user?.id || 'superadmin',
          is_active: 1,
          notes: notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        onAdded(); onClose();
        setEmail(''); setName(''); setNotes('');
        setPerms({ ...DEFAULT_PERMISSIONS });
      } else {
        setError(data.message || 'Failed to create admin');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setSaving(false);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl border border-brand-dark my-4 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-purple-900 to-accent-navy text-white rounded-t-[40px] p-8 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-accent-gold" />
            </div>
            <div>
              <h3 className="font-serif text-2xl">Grant Admin Access</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">Assign Site Administrator role</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto min-h-0 custom-scrollbar">
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Email Address *</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Display Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Permissions</label>
              <div className="flex gap-2">
                <button type="button" onClick={grantAll} className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-xl hover:bg-green-100 transition-colors">Grant All</button>
                <button type="button" onClick={revokeAll} className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-xl hover:bg-red-100 transition-colors">Revoke All</button>
              </div>
            </div>
            <div className="space-y-4">
              {PERMISSION_GROUPS.map(group => (
                <div key={group.group}>
                  <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2">{group.group}</p>
                  <div className="grid grid-cols-1 gap-2">
                    {group.perms.map(perm => (
                      <div key={perm.key}
                        className="flex items-center justify-between p-3 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/40 transition-colors">
                        <div>
                          <p className="text-xs font-bold text-accent-navy">{perm.label}</p>
                          <p className="text-[10px] text-text-muted">{perm.desc}</p>
                        </div>
                        <Toggle active={!!perms[perm.key]} onToggle={() => togglePerm(perm.key)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Internal Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Why is this person being granted admin access?"
              className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-light resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-[11px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg disabled:opacity-60">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiShield} className="w-4 h-4" />}
              {saving ? 'Granting…' : 'Grant Access'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const EditPermissionsModal = ({ isOpen, admin, onClose, onSaved }) => {
  const [perms, setPerms]   = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admin) setPerms(parsePerms(admin.permissions));
  }, [admin]);

  const togglePerm = (key) => setPerms(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await writeFetch(`update/site_admins_1741165000/${admin.id}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: JSON.stringify(perms) }),
      });
      onSaved();
      onClose();
    } catch { /* silent */ }
    setSaving(false);
  };

  if (!isOpen || !admin) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg border border-brand-dark my-4 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="bg-accent-navy text-white rounded-t-[40px] p-7 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-serif text-xl">Edit Permissions</h3>
            <p className="text-white/50 text-[10px] font-black tracking-widest mt-0.5">{admin.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-7 space-y-4 overflow-y-auto min-h-0 custom-scrollbar">
          {PERMISSION_GROUPS.map(group => (
            <div key={group.group}>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2">{group.group}</p>
              <div className="space-y-2">
                {group.perms.map(perm => (
                  <div key={perm.key}
                    className="flex items-center justify-between p-3 bg-brand-light rounded-2xl border border-brand-dark">
                    <div>
                      <p className="text-xs font-bold text-accent-navy">{perm.label}</p>
                      <p className="text-[10px] text-text-muted">{perm.desc}</p>
                    </div>
                    <Toggle active={!!perms[perm.key]} onToggle={() => togglePerm(perm.key)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={onClose} className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-[11px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg disabled:opacity-60">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const AdminCard = ({ admin, onToggleActive, onEdit, onRevoke }) => {
  const perms      = parsePerms(admin.permissions);
  const activePerms = Object.entries(perms).filter(([, v]) => v).length;
  const totalPerms  = ALL_PERMS.length;
  const isActive    = admin.is_active === 1 || admin.is_active === '1';

  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-[28px] border-2 p-6 transition-all ${isActive ? 'border-brand-dark hover:border-accent-gold/40' : 'border-dashed border-brand-dark/40 opacity-60'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${isActive ? 'bg-accent-navy' : 'bg-gray-400'}`}>
            {(admin.name || admin.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-accent-navy text-sm">{admin.name || 'Unnamed Admin'}</p>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border ${isActive ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                {isActive ? 'Active' : 'Suspended'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <SafeIcon icon={FiMail} className="w-3 h-3 text-text-muted" />
              <p className="text-[11px] text-text-muted font-mono">{admin.email}</p>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <SafeIcon icon={FiLock} className="w-3 h-3 text-accent-gold" />
              <p className="text-[10px] text-text-muted">
                <span className="font-bold text-accent-navy">{activePerms}</span>/{totalPerms} permissions active
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => onEdit(admin)} title="Edit permissions"
            className="p-2 rounded-xl border border-brand-dark hover:border-accent-gold hover:text-accent-gold transition-all text-text-muted">
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          </button>
          <button onClick={() => onToggleActive(admin)} title={isActive ? 'Suspend' : 'Reactivate'}
            className={`p-2 rounded-xl border transition-all ${isActive ? 'border-amber-200 text-amber-600 hover:bg-amber-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
            <SafeIcon icon={isActive ? FiToggleRight : FiToggleLeft} className="w-4 h-4" />
          </button>
          <button onClick={() => onRevoke(admin)} title="Revoke access"
            className="p-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition-all">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {PERMISSION_GROUPS.flatMap(g => g.perms).map(perm => (
          <span key={perm.key}
            className={`text-[9px] px-2 py-1 rounded-full font-black uppercase tracking-widest border ${perms[perm.key] ? 'bg-accent-navy/5 border-accent-navy/20 text-accent-navy' : 'bg-gray-50 border-gray-200 text-gray-400 line-through'}`}>
            {perm.label}
          </span>
        ))}
      </div>

      {admin.notes && (
        <p className="mt-3 text-[10px] text-text-muted bg-brand-light rounded-xl px-3 py-2 italic">
          📝 {admin.notes}
        </p>
      )}

      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-text-muted">
        <SafeIcon icon={FiClock} className="w-3 h-3" />
        Granted {new Date(admin.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </motion.div>
  );
};

const SuperAdminPanel = () => {
  const [activeSection, setActiveSection] = useState('admins');
  const [admins, setAdmins]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [filter, setFilter]       = useState('all');

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await writeFetch('read/site_admins_1741165000?sort=created_at&order=desc&limit=100');
      const data = await res.json();
      setAdmins(Array.isArray(data.data) ? data.data : []);
    } catch { setAdmins([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleToggleActive = async (admin) => {
    const newVal = admin.is_active === 1 || admin.is_active === '1' ? 0 : 1;
    await writeFetch(`update/site_admins_1741165000/${admin.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: newVal }),
    });
    fetchAdmins();
  };

  const handleRevoke = async (admin) => {
    if (!window.confirm(`Revoke admin access for ${admin.email}? This cannot be undone.`)) return;
    await writeFetch(`delete/site_admins_1741165000/${admin.id}`, { method: 'DELETE' });
    fetchAdmins();
  };

  const filtered = admins.filter(a => {
    const matchesSearch = !search ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.name?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      (filter === 'active' && (a.is_active === 1 || a.is_active === '1')) ||
      (filter === 'suspended' && (a.is_active === 0 || a.is_active === '0'));
    return matchesSearch && matchesFilter;
  });

  const activeCount    = admins.filter(a => a.is_active === 1 || a.is_active === '1').length;
  const suspendedCount = admins.length - activeCount;

  const SECTIONS = [
    { id: 'admins',        label: 'Manage Admins',      icon: FiShield },
    { id: 'impersonation', label: 'Role Impersonation',  icon: FiZap   },
  ];

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-purple-900 via-accent-navy to-purple-900 text-white rounded-[32px] p-8 flex items-center gap-6 border border-purple-700/30 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiCrown} className="w-8 h-8" />
        </div>
        <div className="z-10">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-serif text-2xl">Super Administrator</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Highest Privilege</span>
          </div>
          <p className="text-white/60 text-sm font-light">
            You have unrestricted control over all platform administrators and their permissions.
            Actions taken here directly affect who can manage the OneFinalLegacy platform.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-brand-dark">
        {SECTIONS.map(sec => (
          <button key={sec.id} onClick={() => setActiveSection(sec.id)}
            className={`flex items-center gap-2 px-6 py-3.5 text-[10px] font-black uppercase tracking-widest
                        transition-all relative ${
              activeSection === sec.id ? 'text-accent-gold' : 'text-text-muted hover:text-accent-navy'
            }`}>
            <SafeIcon icon={sec.icon} className="w-3.5 h-3.5" />
            {sec.label}
            {activeSection === sec.id && (
              <motion.div layoutId="superTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-gold rounded-t-full" />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeSection === 'impersonation' && (
          <motion.div key="impersonation" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <RoleImpersonationSection />
          </motion.div>
        )}

        {activeSection === 'admins' && (
          <motion.div key="admins" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
            <div className="grid grid-cols-3 gap-6">
              {[
                { label: 'Total Admins',     value: admins.length,    color: 'text-accent-navy',  bg: 'bg-accent-navy/5'  },
                { label: 'Active Admins',    value: activeCount,      color: 'text-green-600',    bg: 'bg-green-50'       },
                { label: 'Suspended Admins', value: suspendedCount,   color: 'text-amber-600',    bg: 'bg-amber-50'       },
              ].map(stat => (
                <div key={stat.label} className={`${stat.bg} rounded-[24px] border border-brand-dark p-6 text-center`}>
                  <p className={`text-3xl font-serif font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                {['all', 'active', 'suspended'].map(f => (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${filter === f ? 'bg-accent-navy text-white border-accent-navy' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
                    {f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search admins…"
                    className="pl-10 pr-4 py-2.5 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium w-52" />
                </div>
                <button onClick={fetchAdmins}
                  className="p-2.5 bg-white border-2 border-brand-dark rounded-2xl hover:border-accent-gold transition-all text-text-muted hover:text-accent-gold">
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                </button>
                <button onClick={() => setIsAddOpen(true)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  Add Admin
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading administrators…</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-brand-dark">
                <div className="w-20 h-20 bg-brand-light rounded-3xl flex items-center justify-center mx-auto mb-6 border border-brand-dark/50">
                  <SafeIcon icon={FiShield} className="w-10 h-10 text-text-muted/30" />
                </div>
                <h4 className="text-2xl font-serif text-accent-navy mb-3">No Administrators Found</h4>
                <p className="text-text-muted font-light mb-8">
                  {search ? 'No admins match your search.' : 'Grant admin access to trusted users using the button above.'}
                </p>
                {!search && (
                  <button onClick={() => setIsAddOpen(true)}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg">
                    <SafeIcon icon={FiPlus} className="w-4 h-4" /> Grant First Admin
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnimatePresence>
                  {filtered.map(admin => (
                    <AdminCard
                      key={admin.id}
                      admin={admin}
                      onToggleActive={handleToggleActive}
                      onEdit={a => setEditAdmin(a)}
                      onRevoke={handleRevoke}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AddAdminModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdded={fetchAdmins}
      />
      <EditPermissionsModal
        isOpen={!!editAdmin}
        admin={editAdmin}
        onClose={() => setEditAdmin(null)}
        onSaved={fetchAdmins}
      />
    </div>
  );
};

export default SuperAdminPanel;